/**
 * New Connection page — two-step wizard
 * Step 1: Pick from catalog or choose custom database type
 * Step 2: Configure connection details
 */

import { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { ArrowLeft, ArrowRight, Loader2 } from 'lucide-react'
import { SidebarTrigger } from '@/components/ui/sidebar'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Checkbox } from '@/components/ui/checkbox'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { CatalogCard } from '@/components/discovery/catalog-card'
import { useCreateConnection } from '@/hooks/use-discovery'
import { getCatalogByCategory } from '@/lib/discovery-catalog'
import type { CatalogEntry } from '@/lib/discovery-catalog'
import type { DatabaseType } from '@/types/discovery'

const connectionSchema = z.object({
  name: z.string().min(1, 'Name is required').max(255),
  description: z.string().max(2000).optional(),
  databaseType: z.enum(['POSTGRESQL', 'MSSQL', 'SNOWFLAKE', 'BIGQUERY', 'REDSHIFT', 'DATABRICKS']),
  host: z.string().min(1, 'Host is required').max(500),
  port: z.number().int().min(1).max(65535),
  databaseName: z.string().min(1, 'Database name is required').max(255),
  username: z.string().min(1, 'Username is required').max(255),
  password: z.string().min(1, 'Password is required'),
  sslEnabled: z.boolean(),
})

type ConnectionFormData = z.infer<typeof connectionSchema>

const defaultPorts: Record<DatabaseType, number> = {
  POSTGRESQL: 5432,
  MSSQL: 1433,
  SNOWFLAKE: 443,
  BIGQUERY: 443,
  REDSHIFT: 5439,
  DATABRICKS: 443,
}

const hostPlaceholders: Record<DatabaseType, string> = {
  POSTGRESQL: 'db.example.com',
  MSSQL: 'db.example.com',
  SNOWFLAKE: 'account.snowflakecomputing.com',
  BIGQUERY: 'my-gcp-project-id',
  REDSHIFT: 'cluster.region.redshift.amazonaws.com',
  DATABRICKS: 'dbc-xxxxx.cloud.databricks.com',
}

const passwordLabels: Record<DatabaseType, string> = {
  POSTGRESQL: 'Password',
  MSSQL: 'Password',
  SNOWFLAKE: 'Password',
  BIGQUERY: 'Service Account JSON Key',
  REDSHIFT: 'Password',
  DATABRICKS: 'Personal Access Token',
}

const databaseNameLabels: Record<DatabaseType, string> = {
  POSTGRESQL: 'Database Name',
  MSSQL: 'Database Name',
  SNOWFLAKE: 'Database / Warehouse',
  BIGQUERY: 'Default Dataset',
  REDSHIFT: 'Database Name',
  DATABRICKS: 'Catalog',
}

type WizardStep = 'pick' | 'configure'

export function ConnectionNewPage() {
  const navigate = useNavigate()
  const createConnection = useCreateConnection()

  const [step, setStep] = useState<WizardStep>('pick')
  const [selectedCatalogEntry, setSelectedCatalogEntry] = useState<CatalogEntry | null>(null)
  const [customDatabaseType, setCustomDatabaseType] = useState<DatabaseType | null>(null)
  const [pickerTab, setPickerTab] = useState<string>('catalog')

  const catalogByCategory = getCatalogByCategory()

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<ConnectionFormData>({
    resolver: zodResolver(connectionSchema),
    defaultValues: {
      databaseType: 'POSTGRESQL',
      port: 5432,
      sslEnabled: false,
    },
  })

  const databaseType = watch('databaseType')
  const sslEnabled = watch('sslEnabled')

  const hasSelection = selectedCatalogEntry !== null || customDatabaseType !== null

  const handleCatalogSelect = useCallback(
    (entry: CatalogEntry) => {
      if (entry.comingSoon) return
      setSelectedCatalogEntry(entry)
      setCustomDatabaseType(null)
    },
    []
  )

  const handleCustomTypeChange = useCallback(
    (value: string) => {
      const dbType = value as DatabaseType
      setCustomDatabaseType(dbType)
      setSelectedCatalogEntry(null)
    },
    []
  )

  const handleNext = useCallback(() => {
    if (selectedCatalogEntry?.databaseType) {
      const dbType = selectedCatalogEntry.databaseType
      const portField = selectedCatalogEntry.connectionFields.find((f) => f.name === 'port')
      const sslField = selectedCatalogEntry.connectionFields.find((f) => f.name === 'ssl')
      const hostField = selectedCatalogEntry.connectionFields.find((f) => f.name === 'host')

      reset({
        databaseType: dbType,
        port: typeof portField?.defaultValue === 'number' ? portField.defaultValue : defaultPorts[dbType],
        sslEnabled: sslField?.defaultValue === true,
        name: '',
        description: '',
        host: '',
        databaseName: '',
        username: '',
        password: '',
      })

      // We set these after reset to ensure they stick
      setValue('databaseType', dbType)
      if (typeof portField?.defaultValue === 'number') {
        setValue('port', portField.defaultValue)
      }
      if (hostField?.placeholder) {
        // Just set the placeholder info via the entry, don't pre-fill the value
      }
    } else if (customDatabaseType) {
      reset({
        databaseType: customDatabaseType,
        port: defaultPorts[customDatabaseType],
        sslEnabled: false,
        name: '',
        description: '',
        host: '',
        databaseName: '',
        username: '',
        password: '',
      })
      setValue('databaseType', customDatabaseType)
    }

    setStep('configure')
  }, [selectedCatalogEntry, customDatabaseType, reset, setValue])

  const handleBack = useCallback(() => {
    setStep('pick')
  }, [])

  const handleTypeChange = (value: string) => {
    const dbType = value as DatabaseType
    setValue('databaseType', dbType)
    setValue('port', defaultPorts[dbType])
  }

  const onSubmit = (data: ConnectionFormData) => {
    createConnection.mutate({
      ...data,
      description: data.description || undefined,
    })
  }

  // Resolve host placeholder — prefer catalog entry's host field placeholder
  const resolvedHostPlaceholder =
    selectedCatalogEntry?.connectionFields.find((f) => f.name === 'host')?.placeholder ??
    hostPlaceholders[databaseType]

  return (
    <>
      {/* Header */}
      <header className="flex h-12 shrink-0 items-center gap-2 border-b px-4 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12">
        <div className="flex items-center gap-2">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-2 h-4" />
          <Button
            variant="ghost"
            size="sm"
            className="h-8"
            onClick={() =>
              step === 'configure' ? handleBack() : navigate('/discovery/connections')
            }
          >
            <ArrowLeft className="mr-2 h-3.5 w-3.5" />
            {step === 'configure' ? 'Back' : 'Applications'}
          </Button>
          <h1 className="text-base font-medium">New Application</h1>
        </div>
      </header>

      {/* Content */}
      <div className="flex flex-1 flex-col gap-4 p-4 md:gap-6 md:p-6">
        {/* Step 1 — Pick */}
        {step === 'pick' && (
          <div className="mx-auto w-full max-w-3xl">
            <Card>
              <CardHeader>
                <CardTitle>Choose a Data Source</CardTitle>
                <CardDescription>
                  Select from the catalog or pick a custom database type.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Tabs value={pickerTab} onValueChange={setPickerTab}>
                  <TabsList className="mb-4">
                    <TabsTrigger value="catalog">Catalog</TabsTrigger>
                    <TabsTrigger value="custom">Custom</TabsTrigger>
                  </TabsList>

                  <TabsContent value="catalog" className="space-y-6">
                    {Array.from(catalogByCategory.entries()).map(
                      ([category, entries]) => (
                        <div key={category}>
                          <h3 className="mb-3 text-sm font-medium text-muted-foreground">
                            {category}
                          </h3>
                          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
                            {entries.map((entry) => (
                              <CatalogCard
                                key={entry.id}
                                entry={entry}
                                selected={selectedCatalogEntry?.id === entry.id}
                                onClick={handleCatalogSelect}
                              />
                            ))}
                          </div>
                        </div>
                      )
                    )}
                  </TabsContent>

                  <TabsContent value="custom">
                    <div className="space-y-4">
                      <p className="text-sm text-muted-foreground">
                        Choose a database type to configure manually.
                      </p>
                      <Select
                        value={customDatabaseType ?? ''}
                        onValueChange={handleCustomTypeChange}
                      >
                        <SelectTrigger className="w-full max-w-xs">
                          <SelectValue placeholder="Select database type..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="POSTGRESQL">PostgreSQL</SelectItem>
                          <SelectItem value="MSSQL">SQL Server</SelectItem>
                          <SelectItem value="SNOWFLAKE">Snowflake</SelectItem>
                          <SelectItem value="BIGQUERY">Google BigQuery</SelectItem>
                          <SelectItem value="REDSHIFT">Amazon Redshift</SelectItem>
                          <SelectItem value="DATABRICKS">Databricks</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </TabsContent>
                </Tabs>

                <div className="mt-6 flex justify-end">
                  <Button
                    onClick={handleNext}
                    disabled={!hasSelection}
                  >
                    Next
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Step 2 — Configure */}
        {step === 'configure' && (
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="mx-auto w-full max-w-2xl"
          >
            <Card>
              <CardHeader>
                <CardTitle>
                  Connection Details
                  {selectedCatalogEntry && (
                    <span className="ml-2 text-sm font-normal text-muted-foreground">
                      ({selectedCatalogEntry.displayName})
                    </span>
                  )}
                </CardTitle>
                <CardDescription>
                  Configure the database connection. You can test the connection
                  after saving.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Name */}
                <div className="space-y-2">
                  <Label htmlFor="name">Connection Name</Label>
                  <Input
                    id="name"
                    placeholder="e.g., Production Database"
                    {...register('name')}
                  />
                  {errors.name && (
                    <p className="text-xs text-destructive">{errors.name.message}</p>
                  )}
                </div>

                {/* Description */}
                <div className="space-y-2">
                  <Label htmlFor="description">Description (optional)</Label>
                  <Input
                    id="description"
                    placeholder="Brief description of this database"
                    {...register('description')}
                  />
                </div>

                <Separator />

                {/* Database Type (readonly if from catalog) */}
                <div className="space-y-2">
                  <Label>Database Type</Label>
                  {selectedCatalogEntry ? (
                    <Input
                      value={selectedCatalogEntry.displayName}
                      disabled
                      className="bg-muted"
                    />
                  ) : (
                    <Select value={databaseType} onValueChange={handleTypeChange}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="POSTGRESQL">PostgreSQL</SelectItem>
                        <SelectItem value="MSSQL">SQL Server</SelectItem>
                        <SelectItem value="SNOWFLAKE">Snowflake</SelectItem>
                        <SelectItem value="BIGQUERY">Google BigQuery</SelectItem>
                        <SelectItem value="REDSHIFT">Amazon Redshift</SelectItem>
                        <SelectItem value="DATABRICKS">Databricks</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                </div>

                {/* Host + Port */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-2 space-y-2">
                    <Label htmlFor="host">
                      {databaseType === 'BIGQUERY' ? 'Project ID' : 'Host'}
                    </Label>
                    <Input
                      id="host"
                      placeholder={resolvedHostPlaceholder}
                      {...register('host')}
                    />
                    {errors.host && (
                      <p className="text-xs text-destructive">{errors.host.message}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="port">Port</Label>
                    <Input
                      id="port"
                      type="number"
                      {...register('port', { valueAsNumber: true })}
                    />
                    {errors.port && (
                      <p className="text-xs text-destructive">{errors.port.message}</p>
                    )}
                  </div>
                </div>

                {/* Database Name */}
                <div className="space-y-2">
                  <Label htmlFor="databaseName">
                    {databaseNameLabels[databaseType]}
                  </Label>
                  <Input
                    id="databaseName"
                    placeholder={
                      databaseType === 'SNOWFLAKE'
                        ? 'MY_DB'
                        : databaseType === 'BIGQUERY'
                          ? 'my_dataset'
                          : databaseType === 'DATABRICKS'
                            ? 'main'
                            : 'mydb'
                    }
                    {...register('databaseName')}
                  />
                  {errors.databaseName && (
                    <p className="text-xs text-destructive">
                      {errors.databaseName.message}
                    </p>
                  )}
                </div>

                <Separator />

                {/* Username */}
                <div className="space-y-2">
                  <Label htmlFor="username">Username</Label>
                  <Input
                    id="username"
                    placeholder="admin"
                    {...register('username')}
                  />
                  {errors.username && (
                    <p className="text-xs text-destructive">
                      {errors.username.message}
                    </p>
                  )}
                </div>

                {/* Password */}
                <div className="space-y-2">
                  <Label htmlFor="password">{passwordLabels[databaseType]}</Label>
                  <Input
                    id="password"
                    type={databaseType === 'BIGQUERY' ? 'text' : 'password'}
                    placeholder={
                      databaseType === 'BIGQUERY'
                        ? 'Paste service account JSON key'
                        : databaseType === 'DATABRICKS'
                          ? 'dapi...'
                          : 'Enter password'
                    }
                    {...register('password')}
                  />
                  {errors.password && (
                    <p className="text-xs text-destructive">
                      {errors.password.message}
                    </p>
                  )}
                </div>

                {/* SSL */}
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="sslEnabled"
                    checked={sslEnabled}
                    onCheckedChange={(checked) =>
                      setValue('sslEnabled', checked === true)
                    }
                  />
                  <Label htmlFor="sslEnabled" className="text-sm font-normal">
                    Enable SSL/TLS connection
                  </Label>
                </div>

                <Separator />

                {/* Actions */}
                <div className="flex items-center justify-between pt-2">
                  <Button type="button" variant="ghost" onClick={handleBack}>
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back
                  </Button>
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => navigate('/discovery/connections')}
                    >
                      Cancel
                    </Button>
                    <Button type="submit" disabled={createConnection.isPending}>
                      {createConnection.isPending && (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      )}
                      Save Connection
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </form>
        )}
      </div>
    </>
  )
}
